import Media from './Media';
import {CreativeStatusType} from '../creativeStatus/data/CreativeStatus';
import MediaOwner, {MediaOwnerInput} from '../mediaOwner/data/MediaOwner';
import MediaType, {MediaTypeInput} from '../mediaType/data/MediaType';
import Publication, {PublicationInput} from '../publication/data/Publication';
import Publisher, {PublisherInput} from '../publisher/data/Publisher';
import Region, {RegionInput} from '../region/data/Region';
import calculateSchedule from '../util/calculateSchedule';

export type CreativeInput = {
    id: string;
    creative: string;
    firstAppeared?: string | null;
    lastAppeared?: string | null;
    status?: CreativeStatusType;
    mediaTypeList?: MediaType[] | MediaTypeInput[];
    publicationList?: Publication[] | PublicationInput[];
    regionList?: Region[] | RegionInput[];
    publisherList?: Publisher[] | PublisherInput[];
    mediaOwnerList?: MediaOwner[] | MediaOwnerInput[];
    country?: string | null;
    placementTypes?: string[] | null;
    advertiserDomain?: string | null;
    complianceRiskRating?: 'low' | 'medium' | 'high' | null;
    complianceReason?: string | null;
    aiExtractedTextV1?: string | null;
    aiTranscriptionTextV1?: string | null;
    aiAdTypeV1?: string | null;
    aiCampaignHeadlineV1?: string[] | null;
    aiDemographicsV1?:
        | {
              ageBracket?: string | null;
              ageName?: string | null;
              genderDistribution?: {
                  unspecified?: number | null;
                  female?: number | null;
                  male?: number | null;
              } | null;
          }[]
        | null;
    aiSummaryInsightsV1?:
        | {
              summary150?: string | null;
              summary300?: string | null;
          }[]
        | null;
    aiTagsV1?:
        | {
              advertiser?: string | null;
              industry?: string | null;
              category?: string | null;
              product?: string | null;
          }[]
        | null;
};

export default class Creative extends Media<Creative> {
    private data: CreativeInput;

    constructor(input: CreativeInput) {
        const {id, creative = 'MISSING_CREATIVE'} = input;
        super({id, creative});
        this.data = input;
    }

    static downloadUrl(list: Array<string>): string {
        const endpoint = process.env.BIGDATR_ENTITY_API_ENDPOINT;
        return `${endpoint}/creativeDownload?creativeList=${list.join(',')}`;
    }

    get mediaOwnerList() {
        return (this.data.mediaOwnerList || []).map((bb) => ensure(MediaOwner)(bb));
    }

    get mediaTypeList() {
        return (this.data.mediaTypeList || []).map((bb) => ensure(MediaType)(bb));
    }

    get publicationList() {
        return (this.data.publicationList || []).map((bb) => ensure(Publication)(bb));
    }

    get publisherList() {
        return (this.data.publisherList || []).map((bb) => ensure(Publisher)(bb));
    }

    get regionList() {
        return (this.data.regionList || []).map((bb) => ensure(Region)(bb));
    }

    get placementTypesList() {
        return (this.data.placementTypes || []).map((bb) => bb.toString());
    }

    // ai extracted data

    get aiExtractedText() {
        return this.data.aiExtractedTextV1;
    }

    get aiTranscriptionText() {
        return this.data.aiTranscriptionTextV1;
    }

    get aiCampaignHeadline() {
        return (this.data.aiCampaignHeadlineV1 || []).join(', ');
    }

    get aiSummaryInsights() {
        const allSummaries = this.data.aiSummaryInsightsV1;
        if (!allSummaries) return '';
        return allSummaries
            .map((summary) => {
                if (summary.summary300) return summary.summary300;
                if (summary.summary150) return summary.summary150;
                return '';
            })
            .join('\n');
    }

    get aiDemographics() {
        return this.data.aiDemographicsV1 ?? [];
    }

    //
    // Other attributes

    get firstAppeared(): Date | undefined {
        return this.data.firstAppeared ? new Date(this.data.firstAppeared) : undefined;
    }
    get lastAppeared(): Date | undefined {
        return this.data.lastAppeared ? new Date(this.data.lastAppeared) : undefined;
    }

    get status() {
        return this.data.status ?? 'inactive';
    }

    get statusColor(): string {
        if (this.status === 'new') return 'green';
        if (this.status === 'active') return 'blue';
        return 'muted';
    }

    get aiAdType() {
        return this.data.aiAdTypeV1 || null;
    }

    get nestedAiAdvertisers() {
        const advertisers = (this.data.aiTagsV1 ?? []).flatMap((tag) => {
            if (tag.advertiser) return [tag.advertiser];
            return [];
        });
        return [...new Set(advertisers)];
    }

    get nestedAiIndustries() {
        const industries = (this.data.aiTagsV1 ?? []).flatMap((tag) => {
            if (tag.industry) return [tag.industry];
            return [];
        });
        return [...new Set(industries)];
    }

    get nestedAiCategories() {
        const categories = (this.data.aiTagsV1 ?? []).flatMap((tag) => {
            if (tag.category) return [tag.category];
            return [];
        });
        return [...new Set(categories)];
    }

    get nestedAiProducts() {
        const products = (this.data.aiTagsV1 ?? []).flatMap((tag) => {
            if (tag.product) return [tag.product];
            return [];
        });
        return [...new Set(products)];
    }

    get schedule() {
        return calculateSchedule({
            firstAppeared: this.firstAppeared,
            lastAppeared: this.lastAppeared
        });
    }

    get advertiserDomain() {
        return this.data.advertiserDomain;
    }

    get complianceRiskRating() {
        return this.data.complianceRiskRating;
    }

    get complianceReason() {
        return this.data.complianceReason;
    }

    //
    // Thumnbails
    get croppedThumbnail(): string {
        return this.mediaTypeList.some((mm) => mm.canonicalName === 'Digital Social') // We don't want to tile social creatives
            ? this.thumbnailUrl('w512c16_9t1', 'jpg')
            : this.thumbnailUrl('w512c16_9', 'jpg');
    }

    get croppedThumbnailWebp(): string {
        return this.mediaTypeList.some((mm) => mm.canonicalName === 'Digital Social') // We don't want to tile social creatives
            ? this.thumbnailUrl('w512c16_9t1', 'webp')
            : this.thumbnailUrl('w512c16_9', 'webp');
    }

    //
    // Other helpers
    get to() {
        return `/creative/${this.id}`;
    }

    get publicLink() {
        return `${process.env.BIGDATR_CLIENT_MAIN_HOST}creative/${this.id}`;
    }

    get dataEntryLink() {
        return `${process.env.BIGDATR_CLIENT_DATAENTRY_HOST}creative-tagger/${this.id}`;
    }

    get isRadio() {
        if (this.mediaTypeList.length) {
            // @todo this is here because the activity inbox creates a faux creative out of a campaign
            // Those campagins can have multiple media types and we only want to say it's radio if all of them are.
            return this.mediaTypeList?.every((m) =>
                m.canonicalName.toLowerCase().includes('radio')
            );
        } else {
            return this.creative.endsWith('.mp3');
        }
    }

    get country() {
        return this.data.country ?? 'Australia';
    }

    toTracking() {
        return {
            creativeId: this.id,
            ...(this.nestedAiAdvertisers.length > 0 && {
                advertiserName: this.nestedAiAdvertisers.join(', ')
            })
        };
    }
}

function ensure<C, A>(Clazz: {new (input: A): C}) {
    return function (input: C | A) {
        if (input instanceof Clazz) return input;
        return new Clazz(input as A);
    };
}
